import dvaModelExtend from 'dva-model-extend';
import _ from 'lodash';

import { bindServices } from '@/services/api';
import { createCrudModel } from '@/utils/core/dva';

const namespace = 'aiSkills';
const fetchRoute = 'ai/skill';
const fetchParams = 'AiSkill';
const api = bindServices('aiSkills');

const _model = createCrudModel({
  namespace,
  fetchRoute,
  fetchParams,
  entityName: 'aiSkill',
  api,
  features: { import: false, bulkActions: false, listCache: true },
  state: {
    showImportModal: false,
    importSkillId: null,
    importSourceProvider: null,
    importPreviewResult: null,
    importPreviewRequest: null,
  },
  effects: {
    *fetchCreate({ payload }, { put, call }) {
      try {
        yield put({ type: 'success', payload: { success: false } });
        const res = yield call(api.create, {
          route: fetchRoute,
          data: payload.data,
        });
        yield put({ type: 'clearListCache' });

        // Surface the freshly created skill in `list` so the EntitySelector's
        // inline-create flow can auto-select it: the picker reads
        // `state.aiSkills.list` (its `listSelector`) right after `success`
        // flips to true and selects the first item that isn't already chosen.
        // We merge the submitted form data with the API response so we always
        // have at least `id` + `name`, regardless of the response envelope.
        const created = res?.data || res || {};
        const createdItem = { ...payload.data, ...created };
        if (createdItem.id) {
          yield put({ type: 'prependCreated', payload: createdItem });
        }

        yield put({
          type: 'success',
          payload: { success: true },
        });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          if (e.validationErrors && _.isEmpty(e.validationErrors[0].field)) {
            _.set(e, 'message', e.validationErrors[0].errorMessages[0]);
          }
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
          yield put({
            type: 'errors',
            payload: {
              validationErrors: e.validationErrors || [],
              message: e.message,
            },
          });
        }
      }
    },
    *fetchPublish({ payload }, { put, call }) {
      try {
        yield call(api.apiRoute, {
          route: `${fetchRoute}/publish/${payload.id}`,
          method: 'post',
        });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'aiSkillPublish', payload: { id: payload.id } });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        /* */
      }
    },

    *fetchImportPreview({ payload }, { call, put }) {
      try {
        const body =
          payload.provider === 1
            ? { provider: payload.provider, githubTreeUrl: payload.githubTreeUrl }
            : {
                provider: payload.provider,
                zipContent: payload.zipContent,
                ...(payload.owner != null && payload.owner !== '' && { owner: payload.owner }),
              };
        const res = yield call(api.apiRoute, {
          route: `${fetchRoute}/import/preview`,
          method: 'post',
          data: body,
        });
        yield put({
          type: 'importPreviewResult',
          payload: { preview: res, request: body },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },

    *fetchImport({ payload }, { call, put, select }) {
      try {
        const importSkillId = yield select((state) => state.aiSkills?.importSkillId);
        const body =
          payload.provider === 1
            ? { provider: payload.provider, githubTreeUrl: payload.githubTreeUrl }
            : {
                provider: payload.provider,
                zipContent: payload.zipContent,
                ...(payload.owner != null && payload.owner !== '' && { owner: payload.owner }),
              };
        const route = importSkillId
          ? `${fetchRoute}/import/${importSkillId}`
          : `${fetchRoute}/import`;
        yield call(api.apiRoute, { route, method: 'post', data: body });
        yield put({ type: 'closeImportModal' });
        yield put({ type: 'clearListCache' });
        yield put({ type: 'fetchList', payload: {} });
        yield put({
          type: 'global/showNotificationsGlobal',
          payload: { visible: true, type: 'success' },
        });
      } catch (e) {
        if (e?.message) {
          yield put({
            type: 'global/showNotificationsForm',
            payload: { visible: true, type: 'error', message: e.message },
          });
        }
      }
    },
  },
  reducers: {
    aiSkillPublish(state, { payload }) {
      const list = state.list.map((item) =>
        item.id === payload.id ? { ...item, published: true } : item,
      );
      return { ...state, list };
    },

    openImportModal(state, { payload }) {
      return {
        ...state,
        showImportModal: true,
        importSkillId: payload?.skillId ?? null,
        importSourceProvider: payload?.sourceProvider ?? null,
        importPreviewResult: null,
        importPreviewRequest: null,
      };
    },

    closeImportModal(state) {
      return {
        ...state,
        showImportModal: false,
        importSkillId: null,
        importSourceProvider: null,
        importPreviewResult: null,
        importPreviewRequest: null,
      };
    },

    closePreviewModal(state) {
      return { ...state, importPreviewResult: null, importPreviewRequest: null };
    },

    importPreviewResult(state, { payload }) {
      return {
        ...state,
        importPreviewResult: payload?.preview,
        importPreviewRequest: payload?.request ?? null,
      };
    },

    prependCreated(state, { payload }) {
      const created = {
        ...payload,
        isActive: payload.isActive !== false,
      };
      const withoutDup = state.list.filter((item) => item.id !== created.id);
      return { ...state, list: [created, ...withoutDup] };
    },
  },
});

export default dvaModelExtend(_model, { namespace });
